import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { SiteHeader } from "../components/layout/SiteHeader";
import { SiteFooter } from "../components/layout/SiteFooter";
import { logEvent } from "../utils/analytics";
import { Container } from "../components/layout/Container";

// JSON-LD schema for SEO
const schema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Feuteuristix Container Optimisation Case Studies",
  "description": "Real-world results from logistics teams that use Feuteuristix to reduce freight costs and improve container utilisation.",
  "itemListElement": [
    {
      "@type": "Article",
      "position": 1,
      "name": "Leading Global FMCG — 23% reduction in container freight spend",
      "description": "How a major FMCG manufacturer with 50,000+ MT annual outbound freight reduced container costs by combining optimal container-type selection with automated 3D load planning.",
      "url": "https://feuteuristix.com/case-studies#fmcg-freight-optimisation",
    },
    {
      "@type": "Article",
      "position": 2,
      "name": "Industrial Conglomerate — 18% higher container utilisation",
      "description": "A diversified manufacturing group eliminated chronic container underutilisation across 12 product lines by replacing manual load planning with algorithmic optimisation.",
      "url": "https://feuteuristix.com/case-studies#industrial-utilisation",
    },
    {
      "@type": "Article",
      "position": 3,
      "name": "South Asia 3PL Network — 22% faster outbound planning",
      "description": "A regional third-party logistics provider cut outbound planning time from 4 hours to under 45 minutes per dispatch window using automated container selection.",
      "url": "https://feuteuristix.com/case-studies#3pl-planning-speed",
    },
  ],
};

const cases = [
  {
    id: "fmcg-freight-optimisation",
    tag: "FMCG / Consumer Goods",
    headline: "23% reduction in container freight spend",
    subhead: "Leading Global FMCG Manufacturer",
    volume: "50,000+ MT outbound freight / year",
    icon: "local_shipping",
    color: "from-blue-500/20 to-primary/10",
    border: "border-blue-500/30",
    stats: [
      { label: "Freight cost reduction", value: "23%" },
      { label: "Container utilisation", value: "+19 pp" },
      { label: "SKUs optimised", value: "2,000+" },
    ],
    problem: "The company operated one of the world's largest outbound FMCG networks with over 2,000 SKUs across multiple container types (20ft, 40ft, 40HQ). Planners manually matched cargo to containers based on experience and rough CBM estimates. This consistently led to 60–70% utilisation — leaving 30–40% of every container empty while paying full freight rates. On high-volume lanes to the Middle East and South-East Asia, the cost leakage ran into millions monthly.",
    solution: "Feuteuristix replaced manual estimation with a cost-optimisation engine that evaluates every possible container combination — single 40HQ vs two 20fts vs mixed fleet — and selects the cheapest option that physically fits the cargo within weight and stacking constraints. Planners receive a 3D load plan for each shipment that the loading team can execute without further interpretation.",
    results: [
      "Container freight spend reduced by 23% on optimised lanes within 90 days of deployment.",
      "Average container utilisation improved from 67% to 86% across all routes.",
      "Planning time per shipment cut from 45 minutes to under 5 minutes.",
      "Load rejections at port dropped to near zero due to accurate weight distribution in the 3D plan.",
    ],
  },
  {
    id: "industrial-utilisation",
    tag: "Manufacturing / Industrial",
    headline: "18% higher container utilisation across 12 product lines",
    subhead: "Diversified Industrial Conglomerate",
    volume: "Multi-plant, cross-border exports",
    icon: "precision_manufacturing",
    color: "from-cyan-500/20 to-blue-500/10",
    border: "border-cyan-500/30",
    stats: [
      { label: "Utilisation improvement", value: "+18 pp" },
      { label: "Freight cost per unit", value: "−14%" },
      { label: "Planning errors eliminated", value: "~100%" },
    ],
    problem: "Across 12 distinct product lines — ranging from heavy industrial components to lightweight finished goods — each plant maintained its own container planning process. Without a unified optimisation layer, containers were booked based on conservative estimates that left significant dead space. Mixed-product shipments (standard in multi-plant consolidation) were particularly problematic: planners had no reliable way to verify that different items packed together within weight limits.",
    solution: "A single Feuteuristix integration across all plants standardised container selection and load planning. The engine handles heterogeneous cargo mixes — different dimensions, weights, and stacking restrictions in the same container — and produces a consolidated plan showing exactly where each item sits, layer by layer. Weight distribution validation is automatic, eliminating the manual cross-checks that previously slowed each shipment.",
    results: [
      "Overall container utilisation improved by 18 percentage points within one quarter.",
      "Freight cost per unit shipped dropped 14% as fewer containers moved the same volume.",
      "Cross-plant consolidation increased by 3× as planners gained confidence in mixed-load verification.",
      "Zero overweight incidents after deployment; previously, 6–8% of shipments required container reloading at port.",
    ],
  },
  {
    id: "3pl-planning-speed",
    tag: "3PL / Freight Forwarding",
    headline: "22% faster outbound planning — 4 hours to 45 minutes",
    subhead: "Regional 3PL Network, South Asia",
    volume: "Multi-city lanes, daily dispatch windows",
    icon: "warehouse",
    color: "from-violet-500/20 to-cyan-500/10",
    border: "border-violet-500/30",
    stats: [
      { label: "Planning time reduction", value: "−82%" },
      { label: "Vehicle utilisation", value: "+22%" },
      { label: "Dispatch SLA misses", value: "−90%" },
    ],
    problem: "A regional 3PL managing dispatch across six cities ran a 4-hour morning planning window to consolidate client cargo into outbound trucks and containers. With dozens of clients shipping different cargo types daily, the planning team manually sorted consignments into loads — a process prone to errors, overtime, and SLA breaches when volumes spiked. The cost: missed dispatch windows, partial loads leaving the yard, and client penalties.",
    solution: "Feuteuristix automated scenario selection for the daily dispatch window. The system ingests all pending consignments, runs container combination and packing optimisation across all vehicle types simultaneously, and outputs an executable load plan per vehicle in under two minutes. Dispatchers review and confirm rather than build from scratch.",
    results: [
      "Daily planning cycle cut from 4 hours to 45 minutes — an 82% reduction.",
      "Vehicle utilisation improved by 22% as algorithmic consolidation eliminated partial loads.",
      "Dispatch SLA misses dropped 90% in the first month after deployment.",
      "The 3PL was able to take on 30% more client volume without adding planning headcount.",
    ],
  },
];

function StatBadge({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-white/10 bg-white/5 px-5 py-4">
      <span className="text-2xl font-black text-white">{value}</span>
      <span className="text-xs font-semibold text-slate-400">{label}</span>
    </div>
  );
}

function CaseStudyCard({ c }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article id={c.id} className={`rounded-2xl border ${c.border} bg-gradient-to-br ${c.color} p-8 md:p-10 scroll-mt-28`}>
      {/* Tag + icon */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-bold uppercase tracking-widest text-slate-400">
          <span className="material-symbols-outlined text-[14px] text-primary">{c.icon}</span>
          {c.tag}
        </span>
        <span className="text-xs text-slate-500">{c.volume}</span>
      </div>

      {/* Result headline */}
      <h2 className="mt-5 text-2xl font-black leading-snug text-white md:text-3xl">{c.headline}</h2>
      <p className="mt-1 text-base font-semibold text-slate-400">{c.subhead}</p>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-3 gap-3">
        {c.stats.map((s) => <StatBadge key={s.label} {...s} />)}
      </div>

      {/* Problem / Solution / Results */}
      <div className="mt-8 space-y-6">
        <div>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">The Problem</h3>
          <p className="text-slate-300 leading-relaxed">{c.problem}</p>
        </div>

        {expanded && (
          <>
            <div>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">The Solution</h3>
              <p className="text-slate-300 leading-relaxed">{c.solution}</p>
            </div>
            <div>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Results</h3>
              <ul className="space-y-2">
                {c.results.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-slate-300">
                    <span className="material-symbols-outlined mt-0.5 shrink-0 text-[16px] text-emerald-400" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>

      <button
        onClick={() => setExpanded((p) => !p)}
        className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-primary hover:text-cyan-400 transition-colors"
      >
        {expanded ? 'Show less' : 'Read full case study'}
        <span className={`material-symbols-outlined text-[16px] transition-transform ${expanded ? 'rotate-180' : ''}`}>expand_more</span>
      </button>
    </article>
  );
}

export function CaseStudiesPage() {
  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-dark text-white">
      <Helmet>
        <title>Case Studies — Container Optimisation Results | Feuteuristix</title>
        <meta name="description" content="Real results: 23% freight cost reduction for a leading global FMCG, 18% higher utilisation for an industrial conglomerate, 82% faster planning for a 3PL network." />
        <meta property="og:title" content="Case Studies — Container Optimisation Results | Feuteuristix" />
        <meta property="og:description" content="Real results: 23% freight cost reduction for a leading global FMCG, 18% higher utilisation for an industrial conglomerate, 82% faster planning for a 3PL network." />
        <meta property="og:url" content="https://feuteuristix.com/case-studies" />
        <meta name="twitter:title" content="Case Studies — Container Optimisation Results | Feuteuristix" />
        <meta name="twitter:description" content="Real results: 23% freight cost reduction for a leading global FMCG, 18% higher utilisation for an industrial conglomerate, 82% faster planning for a 3PL network." />
        <link rel="canonical" href="https://feuteuristix.com/case-studies" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      {/* JSON-LD schema — also inline for crawlers that don't execute JS */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <SiteHeader />

      <main className="flex-1 pt-28 pb-16">
        <Container>

          {/* Hero */}
          <section className="mb-16 max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Case Studies</p>
            <h1 className="mt-4 text-4xl font-black leading-tight md:text-5xl">
              Proven impact across logistics operations.
            </h1>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed">
              Logistics teams using Feuteuristix's container optimisation engine consistently reduce freight costs, improve capacity utilisation, and eliminate manual planning errors. These are real results from production deployments.
            </p>

            {/* Aggregate stats */}
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { v: "23%",    l: "Average freight cost reduction" },
                { v: "86%",    l: "Avg container utilisation achieved" },
                { v: "< 5 min", l: "Planning time per shipment" },
              ].map(({ v, l }) => (
                <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-center">
                  <p className="text-3xl font-black text-white">{v}</p>
                  <p className="mt-1 text-xs text-slate-400">{l}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Case study cards */}
          <section className="space-y-10">
            {cases.map((c) => <CaseStudyCard key={c.id} c={c} />)}
          </section>

          {/* Deep dive teaser — HUL learnings */}
          <section className="mt-16 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-8 md:p-12">
            <div className="flex items-start gap-4">
              <span className="material-symbols-outlined mt-1 text-3xl text-primary shrink-0">article</span>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary">Deep Dive — Coming Soon</p>
                <h2 className="mt-2 text-2xl font-black text-white md:text-3xl">
                  How we reduced freight costs for a Fortune 500 FMCG manufacturer
                </h2>
                <p className="mt-3 text-slate-300 leading-relaxed max-w-2xl">
                  A full technical and operational case study: the algorithm design decisions, the data we analysed across thousands of historical shipments, the exact changes to container mix strategy, and the measurable savings achieved across multiple trade lanes. Including what didn't work, and why.
                </p>
                <p className="mt-5 text-sm text-slate-400">
                  Get notified when published —{" "}
                  <a
                    href="mailto:contact@feuteuristix.com?subject=Case study notification"
                    className="text-primary hover:underline"
                  >
                    email us
                  </a>
                  {" "}or{" "}
                  <Link
                    to="/3d-cargo-load-simulator"
                    onClick={() => logEvent({ name: 'case_studies_inline_cta', type: 'cta' })}
                    className="text-primary hover:underline"
                  >
                    create a free account
                  </Link>
                  {" "}and we'll reach out directly.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="mt-16 text-center">
            <h2 className="text-2xl font-black text-white md:text-3xl">Want results like these?</h2>
            <p className="mt-3 text-slate-300">Start with 10 free simulations — no card required.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <Link
                to="/3d-cargo-load-simulator"
                onClick={() => logEvent({ name: 'case_studies_bottom_cta', type: 'cta' })}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-7 py-3 font-bold text-white transition hover:brightness-110"
              >
                Try free
                <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
              </Link>
              <Link
                to="/contact-sales"
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-7 py-3 font-bold text-white transition hover:bg-white/10"
              >
                Talk to us
              </Link>
            </div>
          </section>

        </Container>
      </main>

      <SiteFooter />
    </div>
  );
}
